import { useState, type FormEvent } from "react";
import { activate } from "../../lib/subject-api";

type Props = {
  code: string;
  onActivated: () => void;
};

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string }
  | { kind: "success" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COPY_BY_ERROR: Record<string, string> = {
  not_found: "We couldn't find this interview. Double-check the link.",
  wrong_email: "That email doesn't match this invitation.",
  session_completed: "This interview is already complete.",
  session_abandoned: "This interview has timed out. Reach out to the person who sent it.",
  rate_limited: "Too many attempts. Wait a moment and try again.",
};

export function ActivationForm({ code, onActivated }: Props) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!normalized || !EMAIL_RE.test(normalized)) {
      setStatus({ kind: "error", message: "Please enter a valid email." });
      return;
    }
    setStatus({ kind: "submitting" });
    const result = await activate({ code, email: normalized });

    if (result.ok) {
      setStatus({ kind: "success" });
      onActivated();
      return;
    }

    const msg =
      result.status === 404
        ? COPY_BY_ERROR.not_found
        : result.status === 403
          ? COPY_BY_ERROR.wrong_email
          : result.status === 410
            ? result.error === "session_abandoned"
              ? COPY_BY_ERROR.session_abandoned
              : COPY_BY_ERROR.session_completed
            : result.status === 429
              ? COPY_BY_ERROR.rate_limited
              : "Something went wrong. Try again in a moment.";
    setStatus({ kind: "error", message: msg });
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className="w-full max-w-md space-y-6"
      aria-label="Begin interview"
    >
      <div className="space-y-2">
        <label htmlFor="email" className="block text-sm font-medium text-ink-700">
          Your email
        </label>
        <input
          id="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status.kind === "submitting"}
          placeholder="you@example.com"
          className="w-full px-4 py-3 rounded-lg bg-parchment-100 border border-parchment-300 text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 focus:ring-offset-parchment-50"
        />
        <p className="text-xs text-ink-500">
          We use this only to confirm it's you. We won't send marketing.
        </p>
      </div>

      {status.kind === "error" && (
        <p className="text-sm text-danger-600" role="alert">
          {status.message}
        </p>
      )}

      <button
        type="submit"
        disabled={status.kind === "submitting"}
        className="w-full px-6 py-3 rounded-lg bg-accent-600 text-parchment-50 font-medium hover:bg-accent-700 disabled:opacity-50 transition-colors"
      >
        {status.kind === "submitting" ? "Confirming…" : "Begin"}
      </button>
    </form>
  );
}
